import type { IAppConfigResponse } from '@modules/config/types';
import type { IContextMutationResult } from '@modules/core/services/query/types';
import { CONFIG_QUERY_KEY } from '@modules/config/constants';
import { useRestClient } from '@modules/core/services/api';
import { useContextMutation } from '@modules/core/services/query';
import { useToastStore } from '@modules/toast/store';

import type {
  ISettingsRegistrationFieldsPayload,
  ISettingsRegistrationFieldsResponse,
} from '../types';
import {
  ADMIN_GENERAL_SETTINGS_REGISTRATION_FIELDS_PERMISSIONS,
  SETTINGS_REGISTRATION_FIELDS_LIST_QUERY_KEY,
  SETTINGS_REGISTRATION_FIELDS_SAVE_MUTATION_KEY,
  SETTINGS_REGISTRATION_FIELDS_SAVE_MUTATION_URL,
} from '../constants';

export default function useSettingsRegistrationFieldsSaveMutation(): IContextMutationResult<
  void,
  Error,
  ISettingsRegistrationFieldsPayload
> {
  const { fetchPost } = useRestClient();
  const { displayPresetToast } = useToastStore();

  return useContextMutation<
    void,
    Error,
    ISettingsRegistrationFieldsPayload
  >({
    mutationKey: [SETTINGS_REGISTRATION_FIELDS_SAVE_MUTATION_KEY],
    mutationFn: ({ actionType, ...payload }) => fetchPost<void>(
      SETTINGS_REGISTRATION_FIELDS_SAVE_MUTATION_URL,
      payload,
      {
        permission: ADMIN_GENERAL_SETTINGS_REGISTRATION_FIELDS_PERMISSIONS.PROJECTSETTINGS_REQUIRED_FIELDS_IN_PROFILE_UPDATE,
        urlParams: {
          actionType,
        },
      },
    ),
    async onSuccess({ queryClient }) {
      await queryClient.invalidateQueries({ queryKey: [SETTINGS_REGISTRATION_FIELDS_LIST_QUERY_KEY] });

      const data = queryClient.getQueryData<ISettingsRegistrationFieldsResponse>([SETTINGS_REGISTRATION_FIELDS_LIST_QUERY_KEY]);

      queryClient.setQueryData<IAppConfigResponse>([CONFIG_QUERY_KEY], (queryData) => {
        if (data?.affiliate_checked && queryData) {
          return {
            ...queryData,
            required_fields_in_profile_update_affiliate: data.affiliate_checked.map(({ id, value, check }) => ({ id, value, check })),
          };
        }
        return queryData;
      });

      displayPresetToast('edited');
    },
  });
}
